import gql from "graphql-tag";

import type {UserRole} from "@khan/auth-shared";
import {formUrlEncode, khanFetch} from "@khan/data-fetch";
import {type GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import type {encodeAppleLoginStateType} from "./__graphql-types__/index.ts";

const LoginCompletePath = "/login/apple/complete";

// sync-start:state-util-action 2134646289 https://github.com/Khan/webapp/blob/master/services/users/auth/third_party_auth_action.go
export type Action = "LOGIN" | "CONNECT" | "REAUTH" | "LINK";
// sync-end:state-util-action

interface GetCompletionResponse {
    appleAuthUrl: string;
    redirectUri: string;
    state: string;
}

export type AppleLoginResponse =
    | {
          nativeSignIn: true;
      }
    // @ts-expect-error - TS2709 - Cannot use namespace 'SignInResponse' as a type.
    | (SignInResponse & {
          // @ts-expect-error - TS2709 - Cannot use namespace 'Authorization' as a type.
          authorization: Authorization & {
              state: string;
          };
      });

/**
 * The Sign in with Apple JS SDK hooks into native functionality if running
 * on Safari on recent iOS/macOS. It does not, however, expose this fact to
 * SDK clients.
 * We want to avoid opening a popup when we can infer that native support is
 * available and so attempt to detect supported browsers.
 * The failure mode is that folks who would normally see a popup window flow
 * will now get a redirect flow. It's marginally worse, but detecting native
 * support and _not_ opening a popup seems like a higher priority than a few
 * false negatives.
 */
const _supportsNativeSignin = (userAgent: string): boolean => {
    // CAUTION: Take care when modifying these regular expressions. They
    // match version specific OS and Browser versions and are slightly
    // delicate. If you modify these, please make sure to add accompanying
    // cases to the tests.
    const macOSRegex = /Mac OS X (\d+)_(\d+)/;
    const macOSMatches = userAgent.match(macOSRegex);

    const safariRegex = /Version\/\d+\.\d+(?:\.\d+)?\s+Safari\W/;
    const safariMatches = userAgent.match(safariRegex);

    if (
        macOSMatches &&
        macOSMatches.length >= 3 &&
        parseInt(macOSMatches[1]) >= 10 &&
        parseInt(macOSMatches[2]) >= 15 &&
        safariMatches &&
        safariMatches.length >= 1
    ) {
        // We're on macOS Catalina (10.15) or higher
        return true;
    }

    const iOSRegex = /CPU (?:iPhone )?OS (\d+)_\d+(?:_\d+)? like Mac OS X/;
    const iOSMatches = userAgent.match(iOSRegex);
    if (iOSMatches && iOSMatches.length >= 2 && parseInt(iOSMatches[1]) >= 13) {
        // We're on iOS 13 higher (any browser, as they all use WebKit under
        // the hood).
        return true;
    }

    // If we get this far we assume no native support
    return false;
};

/**
 * Ensures the Sign in with Apple JS SDK is loaded.
 * We import the Sign in with Apple SDK dynamically to avoid it adding to the
 * initial page weight.
 */
const _ensureSDKLoaded = () =>
    import("./appleid.auth.js").then(({default: appleId}) => {
        if (!appleId) {
            throw new Error("AppleID not found");
        }
        appleId.auth.init({
            // sync-start:sign-in-with-apple-client-id 1964893810 https://github.com/Khan/webapp/blob/master/services/users/auth/apple/client_type.go
            clientId: "org.khanacademy.www",
            // sync-end:sign-in-with-apple-client-id
            // We don't really use this, but the Sign in with Apple SDK
            // requires we provide a redirectURI when we init (which we later
            // override when we call `.signIn()`)
            redirectURI: "/login/apple/complete",
            scope: "email name",
        });
        return appleId;
    });

const ENCODE_MUTATION: GqlOperation<
    encodeAppleLoginStateType["response"],
    encodeAppleLoginStateType["variables"]
> = gqlOp(gql`
    mutation encodeAppleLoginState(
        $action: Action!
        $continueUrl: String!
        $role: UserRole
        $signupCodes: [String]
    ) {
        encodeAppleLoginState(
            action: $action
            continueUrl: $continueUrl
            role: $role
            signupCodes: $signupCodes
        ) {
            redirectUri
            state
        }
    }
`);

/**
 * Sign in with Apple matches redirct_uri's (completion urls) exactly, so we
 * have to use the `state` parameter to encode the completion url. To do
 * this, we ask the server to encode the real completion URL for us in a
 * `state` object and then we always give Apple `/login/apple/complete` and
 * Apple returns our `state` object complete with the real completion url.
 */
const _encodeLoginInState = (
    gqlFetch: GqlFetchFn,
    action: Action,
    continueUrl: string,
    role?: UserRole,
    signupCodes?: Array<string>,
): Promise<GetCompletionResponse> => {
    // @ts-expect-error - TS2322 - Type 'Promise<GetCompletionResponse | undefined>' is not assignable to type 'Promise<GetCompletionResponse>'.
    return gqlFetch(ENCODE_MUTATION, {
        variables: {action, continueUrl, role, signupCodes},
    }).then((result) => result.encodeAppleLoginState);
};

/**
 * When the login process uses a popup, we need to send the result from Apple
 * to the server and cause the browser to navigate wherever the server tells
 * it to on success (the continue URL). However, because we've done the login
 * via XHR and the form itself doesn't exist in the page, we need to build it
 * dynamically and POST the data in JS.
 */
// @ts-expect-error - TS2709 - Cannot use namespace 'SignInResponse' as a type.
export const postResponse = (response: SignInResponse) => {
    // This is only used for situations where we use the popup.
    // Without the popup we redirect away before this callback
    // happens.
    // POST this data back to /login/apple/complete
    const form = document.createElement("form");
    form.id = "sign-in-with-apple-completion-form";
    form.action = LoginCompletePath;
    form.method = "post";

    const addFormInput = (name: string, value?: string | null) => {
        if (value == null) {
            return;
        }

        const input = document.createElement("input");
        input.name = name;
        input.hidden = true;
        input.value = value;
        form.appendChild(input);
    };

    Object.keys(response.authorization).forEach(
        // @ts-expect-error - TS2709 - Cannot use namespace 'SignInResponse' as a type.
        (key: keyof SignInResponse["authorization"]) => {
            if (response.authorization.hasOwnProperty(key)) {
                // @ts-expect-error - TS2345 - Argument of type 'string | number | symbol' is not assignable to parameter of type 'string'.
                addFormInput(key, response.authorization[key]);
            }
        },
    );

    // Sign in with Apple only gives us this data the first time the user
    // signs in and authorizes Khan Academy to use Sign in with Apple. After
    // that, this key is always an empty object.
    addFormInput("user", JSON.stringify(response.user));

    document.body?.appendChild(form);
    form.submit();
};

/**
 * Posts the Apple `SignInResponse` to webapp.
 *
 * In cases where we redirect to Apple, Apple will POST back to webapp these
 * parameters, but in cases where we use a popup, we get this response as the
 * return value to the `.login()` call.
 *
 * This method POSTs the values to the same endpoint that Apple does.
 */
export const postResponseViaFetch = (
    // @ts-expect-error - TS2709 - Cannot use namespace 'SignInResponse' as a type.
    response: SignInResponse,
): Promise<Response> => {
    return khanFetch("/login/apple/complete", {
        method: "POST",
        ...formUrlEncode({
            code: response.authorization.code,
            id_token: response.authorization.id_token,
            state: response.authorization.state,
            user: JSON.stringify(response.user != null ? response.user : {}),
        }),
    });
};

/**
 * Decides if we should use a popup for Sign in with Apple.
 *
 * We don't want to use a popup on mobile browsers because that's just
 * offensive (and that's how Google and Facebook logins do things) and we
 * also don't want to show a popup when native Sign in with Apple is
 * supported. We do our best to detect native support, but if we get it
 * wrong, the worst that will happen is the user will get the redirect
 * (mobile style) login flow instead of a popup on desktop.
 */
export const shouldUsePopup = (): boolean =>
    !(
        _supportsNativeSignin(navigator.userAgent) ||
        RequestInfo.isMobile ||
        RequestInfo.isIPad ||
        RequestInfo.embedded
    );

/**
 * Begins the Sign in with Apple authentication process. Uses native Sign in
 * with Apple functionality, if available. Otherwise the user is presented with
 * the Apple login website.
 * A popup is used if the user is on a desktop browser, otherwise they are
 * redirected to the authentication page with the `continue` url set.
 *
 * @param {string} action The action that Sign in with Apple is being used for.
 * @param {string} continueUrl The URL to redirect to after completion
 *                 of Sign in with Apple.
 * @param {string} role (optional) The role to assign to the user signing in,
 *                 if this sign-in results in a new user being created.
 * @param {Array<string>} signupCodes (optional) The list of signup codes to
 *                 process if this sign-in results in a new user being created.
 */
export const launchAppleConnection: (
    gqlFetch: GqlFetchFn,
    action: Action,
    continueUrl: string,
    role?: UserRole,
    signupCodes?: Array<string>,
) => Promise<AppleLoginResponse> = (
    gqlFetch,
    action,
    continueUrl,
    role,
    signupCodes,
) => {
    return Promise.all([
        AppleUtil._ensureSDKLoaded(),
        AppleUtil._encodeLoginInState(
            gqlFetch,
            action,
            continueUrl,
            role,
            signupCodes,
        ),
    ])
        .then(([appleId, completionResult]: [any, any]) => {
            // TODO: Deal with a nonce?
            const signInResult = appleId.auth.signIn({
                redirectURI: completionResult.redirectUri,
                state: completionResult.state,
                usePopup: AppleUtil.shouldUsePopup(),
            });

            // signIn() returns a promise in most cases, however, when native
            // Sign in with Apple (Safari) kicks in, this function returns
            // `undefined` and redirects to the `redirectURI` upon successful
            // authentication.
            return signInResult != null
                ? signInResult.then((response) => [response, completionResult])
                : [{nativeSignIn: true}, completionResult];
        })
        .then(([response, completionResult]: [any, any]) => {
            if (response.nativeSignIn) {
                // We don't need to take any action as we'll be redirecting
                // away as soon as auth finishes.
                return response;
            }

            if (response.error != null) {
                throw new KAError(
                    `Sign in with Apple failed. ${response.error}`,
                    Errors.Internal,
                );
            }
            // At this point, flow should be refining the response down
            // to just SignInResponse, but it isn't. So, let's help it.
            // @ts-expect-error - TS2709 - Cannot use namespace 'SignInResponse' as a type.
            const signInResponse: SignInResponse = response as any;

            return {
                ...signInResponse,
                authorization: {
                    ...signInResponse.authorization,
                    // :( Apple doesn't return the `state` we send it when we
                    // set `usePopup` to true! And when `usePopup` is false,
                    // we never get here because the auth flow uses
                    // redirects.
                    state: completionResult.state,
                },
            };
        });
};

const AppleUtil = {
    _ensureSDKLoaded, // exported for testing
    _encodeLoginInState, // exported for testing
    _supportsNativeSignin, // exported for testing
    launchAppleConnection,
    postResponse,
    postResponseViaFetch,
    shouldUsePopup,
} as const;

export default AppleUtil; // used for testing
